import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// React.lazy itself constrains T as ComponentType<any> (props are
// contravariant; ComponentType<unknown> would reject any concrete prop type).
// Mirror that contract here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LazyWithPreload<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<unknown>;
};

// One-shot reload guard for lazy-chunk failures.
//
// Symptom we are recovering from: a deploy lands while a user's tab is open,
// the service worker activates and swaps the precache, and a subsequent
// import() resolves with a module whose `default` is undefined (because the
// running entry chunk and the freshly precached lazy chunk were built against
// different minified-name namespaces). React's lazy then surfaces this as
// "Element type is invalid. Received a promise that resolves to: undefined."
//
// The fix is straightforward: reload once. After a reload the entry chunk and
// lazy chunks come from the same build, so the next render works. The cooldown
// prevents an infinite reload loop if something is genuinely broken.
const RELOAD_KEY = 'kit-lazy-reload-at';
const RELOAD_COOLDOWN_MS = 60_000;

function reloadOnceAfterChunkFailure(): void {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  const prev = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
  if (Number.isFinite(prev) && Date.now() - prev < RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  // setTimeout so the throw lands in React's error boundary (and Sentry)
  // before the navigation kicks in.
  setTimeout(() => window.location.reload(), 0);
}

// Wraps React.lazy so the underlying dynamic-import can be triggered ahead of
// time (e.g. on pointer-enter for nav links). Calling .preload() multiple times
// is safe — the browser dedupes the module fetch and React's lazy() caches the
// resolved module internally.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithPreload<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyWithPreload<T> {
  const guarded = (): Promise<{ default: T }> =>
    load().then(
      (mod) => {
        if (!mod || typeof mod.default === 'undefined') {
          reloadOnceAfterChunkFailure();
          throw new Error(
            'lazyWithPreload: chunk resolved with undefined default; reloading once',
          );
        }
        return mod;
      },
      (err: unknown) => {
        reloadOnceAfterChunkFailure();
        throw err;
      },
    );
  const Component = lazy(guarded) as LazyWithPreload<T>;
  Component.preload = guarded;
  return Component;
}
